/**
 * @fileoverview Small consent/region readers shared by analytics context helpers.
 * @description Kept separate from `consent.ts` so diagnostic modules don't widen
 * the hot-path import graph.
 *
 * @module lib/analytics/consent-context
 */

import {
  CONSENT_STORAGE_KEY,
  hasExplicitAnalyticsOptIn,
  hasExplicitAnalyticsOptOut,
} from "./consent"
import { REGION_CONSENT_REQUIRED_COOKIE } from "./region"
import type { ConsentPreferences } from "~/types/consent"

/**
 * Whether the middleware region cookie marks this visitor as EEA/UK/CH.
 *
 * @returns {boolean} True when `adsights_region_cr=1`.
 */
export function isConsentRequiredRegionClient(): boolean {
  if (typeof document === "undefined") return false
  try {
    return document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)
  } catch {
    return false
  }
}

/**
 * Tri-state consent label for analytics diagnostics (not a legal record).
 *
 * @returns {'granted' | 'denied' | 'default'} Consent state label for reporting.
 */
export function analyticsConsentState(): "granted" | "denied" | "default" {
  if (typeof window === "undefined") return "default"
  if (hasExplicitAnalyticsOptOut()) return "denied"
  if (hasExplicitAnalyticsOptIn()) return "granted"
  return "default"
}

/**
 * Reads stored consent preferences, if any.
 *
 * @returns {ConsentPreferences | null} Parsed preferences or null.
 */
export function readStoredConsentPreferences(): ConsentPreferences | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ConsentPreferences
  } catch {
    return null
  }
}
